import { AccommodationObject } from "../../accommodation/infrastructure/api/accommodation.api";
import {
  GetAllExtrasRequest,
  GetExtrasRequest,
  GetExtrasResponse,
} from "../../../gen/witbooking/backoffice/extra/v1/extra_api_pb";
import {
  AppliesTo,
  TickerWithPosition,
  UpdateTagAppliesTo,
  UpdateTagsAppliesToRequest,
  UpdateTagsAppliesToResponse,
} from "../../../gen/witbooking/backoffice/tags/v1/tag_api_pb";
import { GrpcCall } from "../../../lib/grpc";
import { TagAssociation } from "../domain/TagAssociation";
import { TagAssociationRepository } from "../domain/TagAssociationRepository";
import { TagRepository } from "../domain/TagRepository";
import {
  toTagAssociationFromAccommodationGrpc,
  toTagAssociationFromExtrasGrpc,
  toTagAssociationFromBundlesGrpc,
  toTagAssociationFromProperty,
} from "./mappers/tagAssociationMapper";
import { toTagAppliesToTypeGrpcFromDomain } from "./mappers/tagMapper";
import {
  BundleType,
  GetBundlesRequest,
  GetBundlesResponse,
} from "../../../gen/witbooking/backoffice/bundle/v1/bundle_api_pb";

export class ApiTagAssociationRepository implements TagAssociationRepository {
  constructor(
    private tagRepository: TagRepository,
    private getAccommodations: () => Promise<AccommodationObject[]>,
    private getExtras: GrpcCall<void, GetExtrasRequest, GetExtrasResponse>,
    private getBundles: GrpcCall<void, GetBundlesRequest, GetBundlesResponse>,
    private updateTagsAppliesTo: GrpcCall<
      void,
      UpdateTagsAppliesToRequest,
      UpdateTagsAppliesToResponse
    >
  ) {}

  async getAll(ticker: string): Promise<TagAssociation[]> {
    const tags = await this.tagRepository.getAll();
    const accommodations = await this.getAccommodations();

    const extras = await this.getExtras(new GetAllExtrasRequest());
    const bundlesRequest = new GetBundlesRequest();
    bundlesRequest.setType(BundleType.ENGINE);
    const bundles = await this.getBundles(bundlesRequest);
    const tagAssociationProperty = toTagAssociationFromProperty(ticker, tags);
    const tagAssociationAccommodations = toTagAssociationFromAccommodationGrpc(
      accommodations,
      tags
    );
    const tagAssociationExtras = toTagAssociationFromExtrasGrpc(extras.getExtrasList(), tags);
    const tagAssociationBundles = toTagAssociationFromBundlesGrpc(bundles.getBundleList(), tags);

    return Promise.resolve([
      tagAssociationProperty,
      ...tagAssociationAccommodations,
      ...tagAssociationExtras,
      ...tagAssociationBundles,
    ]);
  }

  async modify(
    oldTagAssociation: TagAssociation,
    newTagAssociation: TagAssociation
  ): Promise<void> {
    const updateTagAppliesTos: Array<UpdateTagAppliesTo> = [];

    oldTagAssociation.tags.forEach(oldTag => {
      if (!newTagAssociation.tags.includes(oldTag)) {
        const ticker = new TickerWithPosition();
        ticker.setTicker(oldTagAssociation.ticker);
        ticker.setPosition(0);

        const appliesTo = new AppliesTo();
        appliesTo.setType(toTagAppliesToTypeGrpcFromDomain(oldTagAssociation.type));
        appliesTo.setTickersList([ticker]);

        const deletionAppliesTo = new UpdateTagAppliesTo();
        deletionAppliesTo.setTagid(oldTag.id);
        deletionAppliesTo.setDeletionsappliesto(appliesTo);

        updateTagAppliesTos.push(deletionAppliesTo);
      }
    });

    newTagAssociation.tags.forEach((newTag, index) => {
      const ticker = new TickerWithPosition();
      ticker.setTicker(newTagAssociation.ticker);
      ticker.setPosition(index + 1);

      const appliesTo = new AppliesTo();
      appliesTo.setType(toTagAppliesToTypeGrpcFromDomain(newTagAssociation.type));
      appliesTo.setTickersList([ticker]);

      const additionAppliesTo = new UpdateTagAppliesTo();
      additionAppliesTo.setTagid(newTag.id);
      additionAppliesTo.setUpdatesappliesto(appliesTo);

      updateTagAppliesTos.push(additionAppliesTo);
    });

    const request = new UpdateTagsAppliesToRequest();
    request.setTagsappliestoList(updateTagAppliesTos);

    await this.updateTagsAppliesTo(request);
  }
}
